/** Canvas renderer for the Roof Geometry editor. */

import { formatMillimetres, gridStep, toScreen, type ViewTransform } from './canvas';
import type { GeometryDraft, GeometrySelection } from './interaction';
import { segmentLength, type EditorOpening, type EditorPoint, type GeometryModel } from './model';
import { formatDraftMetres } from './dynamicPolygonInput';

export interface LayerVisibility {
  [layerId: string]: boolean;
}

export interface RenderOptions {
  view: ViewTransform;
  width: number;
  height: number;
  layers: LayerVisibility;
  gridSizeMM?: number;
  selection?: GeometrySelection;
  draft?: GeometryDraft;
  hoverPoint?: { x: number; y: number };
  problemObjectIds: readonly string[];
}

interface Palette {
  grid: string;
  gridMajor: string;
  outline: string;
  outlineDraft: string;
  fill: string;
  point: string;
  pointSelected: string;
  pointProblem: string;
  opening: string;
  dimension: string;
  text: string;
  axis: string;
  north: string;
  canvas: string;
}

function readPalette(canvas: HTMLCanvasElement): Palette {
  const styles = getComputedStyle(canvas);
  const token = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  return {
    grid: token('--border-light', '#D9D9D9'),
    gridMajor: token('--border-strong', '#8A8A8A'),
    outline: token('--text-primary', '#000000'),
    outlineDraft: token('--text-muted', '#777777'),
    fill: token('--zone-interior-fill', 'rgba(154,167,174,0.3)'),
    point: token('--text-secondary', '#444444'),
    pointSelected: token('--ejot-red', '#CE1230'),
    pointProblem: token('--status-fail', '#CE1230'),
    opening: token('--zone-edge', '#D68910'),
    dimension: token('--ejot-red', '#CE1230'),
    text: token('--text-primary', '#000000'),
    axis: token('--border-strong', '#8A8A8A'),
    north: token('--ejot-red', '#CE1230'),
    canvas: token('--surface-primary', '#FFFFFF'),
  };
}

export function render(canvas: HTMLCanvasElement, model: GeometryModel, options: RenderOptions): void {
  const context = canvas.getContext('2d');
  if (!context) return;

  const ratio = window.devicePixelRatio || 1;
  const { width, height } = options;
  if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
  }
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);

  const palette = readPalette(canvas);
  const layer = (id: string) => options.layers[id] !== false;

  if (layer('grid')) drawGrid(context, options, palette);
  if (layer('axes')) drawAxes(context, options, palette);
  if (layer('roof-solid') || layer('roof-outline')) drawRoof(context, model, options, palette);
  if (layer('openings')) drawOpenings(context, model.openings, options, palette);
  if (layer('overall-dimensions')) drawSegmentLabels(context, model, options, palette);
  if (layer('north-arrow')) drawNorthArrow(context, model, options, palette);
  drawPoints(context, model, options, palette);
  if (options.draft) drawDraft(context, options.draft, options, palette);
}

function drawGrid(context: CanvasRenderingContext2D, options: RenderOptions, palette: Palette): void {
  const { view, width, height } = options;
  const step = Number.isFinite(options.gridSizeMM) && (options.gridSizeMM ?? 0) > 0
    ? options.gridSizeMM!
    : gridStep(view.scale);
  const majorEvery = 5;
  const left = view.offsetX;
  const bottom = view.offsetY;
  const right = left + width / view.scale;
  const top = bottom + height / view.scale;

  context.lineWidth = 1;
  const firstX = Math.floor(left / step) * step;
  for (let x = firstX; x <= right; x += step) {
    const major = Math.round(x / step) % majorEvery === 0;
    context.strokeStyle = major ? palette.gridMajor : palette.grid;
    context.globalAlpha = major ? 0.45 : 0.25;
    const screen = toScreen(view, { x, y: 0 }, height);
    context.beginPath();
    context.moveTo(Math.round(screen.x) + 0.5, 0);
    context.lineTo(Math.round(screen.x) + 0.5, height);
    context.stroke();
  }

  const firstY = Math.floor(bottom / step) * step;
  for (let y = firstY; y <= top; y += step) {
    const major = Math.round(y / step) % majorEvery === 0;
    context.strokeStyle = major ? palette.gridMajor : palette.grid;
    context.globalAlpha = major ? 0.45 : 0.25;
    const screen = toScreen(view, { x: 0, y }, height);
    context.beginPath();
    context.moveTo(0, Math.round(screen.y) + 0.5);
    context.lineTo(width, Math.round(screen.y) + 0.5);
    context.stroke();
  }
  context.globalAlpha = 1;
}

function drawAxes(context: CanvasRenderingContext2D, options: RenderOptions, palette: Palette): void {
  const { view, width, height } = options;
  const origin = toScreen(view, { x: 0, y: 0 }, height);
  context.strokeStyle = palette.axis;
  context.lineWidth = 1.5;
  context.globalAlpha = 0.7;
  context.beginPath();
  context.moveTo(0, Math.round(origin.y) + 0.5);
  context.lineTo(width, Math.round(origin.y) + 0.5);
  context.moveTo(Math.round(origin.x) + 0.5, 0);
  context.lineTo(Math.round(origin.x) + 0.5, height);
  context.stroke();
  context.globalAlpha = 1;
}

function traceRing(context: CanvasRenderingContext2D, points: readonly { x: number; y: number }[], view: ViewTransform, height: number): void {
  points.forEach((point, index) => {
    const screen = toScreen(view, point, height);
    if (index === 0) context.moveTo(screen.x, screen.y);
    else context.lineTo(screen.x, screen.y);
  });
  if (points.length > 2) context.closePath();
}

function drawRoof(context: CanvasRenderingContext2D, model: GeometryModel, options: RenderOptions, palette: Palette): void {
  if (model.points.length === 0) return;
  const { view, height, layers } = options;

  context.beginPath();
  traceRing(context, model.points, view, height);
  if (model.closed) {
    for (const opening of model.openings) traceRing(context, opening.points, view, height);
    if (layers['roof-solid'] !== false) {
      context.fillStyle = palette.fill;
      context.fill('evenodd');
    }
  }

  context.beginPath();
  traceRing(context, model.points, view, height);
  context.strokeStyle = model.closed ? palette.outline : palette.outlineDraft;
  context.lineWidth = model.closed ? 2 : 1.5;
  if (!model.closed) context.setLineDash([6, 4]);
  context.stroke();
  context.setLineDash([]);

  const selected = options.selection;
  if (selected?.kind === 'segment' && selected.owner.kind === 'roof' && model.closed) {
    drawSelectedSegment(context, model.points, selected.segmentIndex, options, palette);
  }
}

function drawOpenings(context: CanvasRenderingContext2D, openings: readonly EditorOpening[], options: RenderOptions, palette: Palette): void {
  const problems = new Set(options.problemObjectIds);
  for (const opening of openings) {
    if (opening.points.length < 2) continue;
    const selectedOpening = options.selection?.kind === 'opening' && options.selection.openingId === opening.id;
    const selectedSegment = options.selection?.kind === 'segment'
      && options.selection.owner.kind === 'opening'
      && options.selection.owner.openingId === opening.id;

    context.beginPath();
    traceRing(context, opening.points, options.view, options.height);
    context.strokeStyle = problems.has(opening.id) ? palette.pointProblem : selectedOpening ? palette.pointSelected : palette.opening;
    context.lineWidth = selectedOpening ? 3 : 1.5;
    context.stroke();

    if (selectedSegment && options.selection?.kind === 'segment') {
      drawSelectedSegment(context, opening.points, options.selection.segmentIndex, options, palette);
    }
  }
}

function drawSelectedSegment(context: CanvasRenderingContext2D, points: readonly EditorPoint[], index: number, options: RenderOptions, palette: Palette): void {
  const start = points[index];
  const end = points[(index + 1) % points.length];
  if (!start || !end) return;
  const a = toScreen(options.view, start, options.height);
  const b = toScreen(options.view, end, options.height);
  context.strokeStyle = palette.pointSelected;
  context.lineWidth = 4;
  context.beginPath();
  context.moveTo(a.x, a.y);
  context.lineTo(b.x, b.y);
  context.stroke();
}

function drawPoints(context: CanvasRenderingContext2D, model: GeometryModel, options: RenderOptions, palette: Palette): void {
  const problems = new Set(options.problemObjectIds);
  const selectedId = options.selection?.kind === 'vertex' ? options.selection.pointId : undefined;

  const drawPoint = (candidate: EditorPoint) => {
    const screen = toScreen(options.view, candidate, options.height);
    const selected = candidate.id === selectedId;
    const problem = problems.has(candidate.id);
    context.beginPath();
    context.arc(screen.x, screen.y, selected ? 6 : 4, 0, Math.PI * 2);
    context.fillStyle = problem ? palette.pointProblem : selected ? palette.pointSelected : palette.point;
    context.fill();
    if (selected || problem) {
      context.strokeStyle = palette.canvas;
      context.lineWidth = 1.5;
      context.stroke();
    }
  };

  model.points.forEach(drawPoint);
  model.openings.forEach((opening) => opening.points.forEach(drawPoint));
}

function drawSegmentLabels(context: CanvasRenderingContext2D, model: GeometryModel, options: RenderOptions, palette: Palette): void {
  if (!model.closed || model.points.length < 3) return;
  const { view, height } = options;
  context.font = '11px "Roboto Condensed", Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  for (let index = 0; index < model.points.length; index += 1) {
    const a = model.points[index];
    const b = model.points[(index + 1) % model.points.length];
    if (!a || !b) continue;
    const length = segmentLength(a, b);
    if (length * view.scale < 40) continue;
    const sa = toScreen(view, a, height);
    const sb = toScreen(view, b, height);
    const midX = (sa.x + sb.x) / 2;
    const midY = (sa.y + sb.y) / 2;
    const dx = sb.x - sa.x;
    const dy = sb.y - sa.y;
    const norm = Math.hypot(dx, dy) || 1;
    const x = midX + (dy / norm) * 14;
    const y = midY - (dx / norm) * 14;
    const label = formatMillimetres(length);
    const metrics = context.measureText(label);
    context.fillStyle = palette.canvas;
    context.globalAlpha = 0.85;
    context.fillRect(x - metrics.width / 2 - 3, y - 8, metrics.width + 6, 16);
    context.globalAlpha = 1;
    context.fillStyle = palette.dimension;
    context.fillText(label, x, y);
  }
}

function drawNorthArrow(context: CanvasRenderingContext2D, model: GeometryModel, options: RenderOptions, palette: Palette): void {
  const x = options.width - 44;
  const y = 44;
  const radius = 20;
  const angle = (-model.northOrientationDegrees * Math.PI) / 180;

  context.save();
  context.translate(x, y);
  context.rotate(angle);
  context.strokeStyle = palette.north;
  context.fillStyle = palette.north;
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(0, -radius);
  context.lineTo(6, radius * 0.6);
  context.lineTo(0, radius * 0.3);
  context.lineTo(-6, radius * 0.6);
  context.closePath();
  context.fill();
  context.restore();

  context.fillStyle = palette.text;
  context.font = '700 10px "Roboto Flex", Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('N', x, y + radius + 10);
}

function drawDraft(context: CanvasRenderingContext2D, draft: GeometryDraft, options: RenderOptions, palette: Palette): void {
  context.save();
  context.strokeStyle = palette.pointSelected;
  context.fillStyle = palette.pointSelected;
  context.lineWidth = 1.5;
  context.setLineDash([6, 4]);

  if (draft.kind === 'rectangle') {
    const minX = Math.min(draft.start.x, draft.end.x);
    const minY = Math.min(draft.start.y, draft.end.y);
    const maxX = Math.max(draft.start.x, draft.end.x);
    const maxY = Math.max(draft.start.y, draft.end.y);
    context.beginPath();
    traceRing(context, [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }], options.view, options.height);
    context.stroke();

    const labelPoint = toScreen(options.view, draft.end, options.height);
    context.setLineDash([]);
    context.font = '11px "Roboto Condensed", Arial, sans-serif';
    context.textAlign = 'left';
    context.textBaseline = 'bottom';
    context.fillText(`${formatMillimetres(Math.abs(maxX - minX))} × ${formatMillimetres(Math.abs(maxY - minY))}`, labelPoint.x + 10, labelPoint.y - 8);
  } else if (draft.points.length > 0) {
    context.beginPath();
    draft.points.forEach((candidate, index) => {
      const screen = toScreen(options.view, candidate, options.height);
      if (index === 0) context.moveTo(screen.x, screen.y);
      else context.lineTo(screen.x, screen.y);
    });
    if (draft.cursor) {
      const cursor = toScreen(options.view, draft.cursor, options.height);
      context.lineTo(cursor.x, cursor.y);
    }
    context.stroke();

    context.setLineDash([]);
    for (const candidate of draft.points) {
      const screen = toScreen(options.view, candidate, options.height);
      context.beginPath();
      context.arc(screen.x, screen.y, 4, 0, Math.PI * 2);
      context.fill();
    }

    const last = draft.points[draft.points.length - 1];
    if (last && draft.cursor) {
      const cursor = toScreen(options.view, draft.cursor, options.height);
      const length = Math.hypot(draft.cursor.x - last.x, draft.cursor.y - last.y);
      context.font = '11px "Roboto Condensed", Arial, sans-serif';
      context.textAlign = 'left';
      context.textBaseline = 'bottom';
      context.fillText(formatDraftMetres(length), cursor.x + 10, cursor.y - 6);
    }
  }
  context.restore();
}
